/**
 * Billable Item Templates - React Query Hooks
 * Phase 8 Batch 8.6 - Billable Item Templates
 *
 * Hooks for managing tenant-scoped billable item template library.
 * Used in Settings page and project forms for quick-add functionality.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export type BillableItemCategory =
  | 'professional_fee'
  | 'add_service'
  | 'billable_expense'
  | 'pass_through'
  | 'internal_work';

export interface BillableItemTemplate {
  id: string;
  tenant_id: string;
  description: string;
  suggested_amount: number;
  deliverables: string | null;
  category: BillableItemCategory;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BillableItemTemplateCreate {
  description: string;
  suggested_amount: number;
  deliverables?: string | null;
  category: BillableItemCategory;
  display_order?: number;
}

export interface BillableItemTemplateUpdate {
  description?: string;
  suggested_amount?: number;
  deliverables?: string | null;
  category?: BillableItemCategory;
  display_order?: number;
  is_active?: boolean;
}

/**
 * Query: Get all billable item templates for current tenant
 */
export function useLibraryTemplates() {
  return useQuery({
    queryKey: ['billable-item-templates'],
    queryFn: () => api.billableItemTemplates.list(),
  });
}

/**
 * Mutation: Create new billable item template
 */
export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BillableItemTemplateCreate) =>
      api.billableItemTemplates.create(data),
    onSuccess: async () => {
      // CRITICAL: Use refetchQueries (not invalidateQueries) for bulk creation scenarios
      // invalidateQueries only refetches if actively observed, but rapid successive
      // invalidations can cause race conditions where the last refetch doesn't capture
      // all newly created templates. refetchQueries forces immediate refetch.
      await queryClient.refetchQueries({ queryKey: ['billable-item-templates'] });
      toast.success('Billable item template added');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add billable item template');
    },
  });
}

/**
 * Mutation: Update billable item template
 */
export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & BillableItemTemplateUpdate) =>
      api.billableItemTemplates.update(id, data),
    onSuccess: async () => {
      // CRITICAL: await to ensure refetch completes before UI updates
      await queryClient.invalidateQueries({ queryKey: ['billable-item-templates'] });
      toast.success('Billable item template updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update billable item template');
    },
  });
}

/**
 * Mutation: Delete billable item template (soft delete)
 */
export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.billableItemTemplates.delete(id),
    onSuccess: async () => {
      // CRITICAL: await to ensure refetch completes before UI updates
      await queryClient.invalidateQueries({ queryKey: ['billable-item-templates'] });
      toast.success('Billable item template deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete billable item template');
    },
  });
}
